import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCircleHalfStroke,
  faCircleCheck,
  faMinus,
  faSlash,
  faChevronDown,
  faChevronRight,
  faShieldHalved,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useProcedureExecutionStore } from '@/stores/procedure-execution.store';
import { StepExecutionBody } from './StepExecutionBody';
import type { StepExecutionView } from '../../services/procedure-execution.service';

const STATUS_CONFIG = {
  NOT_STARTED: { icon: faCircle, label: 'Not Started', cls: 'text-muted-foreground/50' },
  IN_PROGRESS: { icon: faCircleHalfStroke, label: 'In Progress', cls: 'text-amber-500' },
  COMPLETED: { icon: faCircleCheck, label: 'Completed', cls: 'text-green-600' },
  SKIPPED: { icon: faMinus, label: 'Skipped', cls: 'text-slate-500' },
  NOT_APPLICABLE: { icon: faSlash, label: 'N/A', cls: 'text-slate-400' },
} as const;

interface Props {
  inspectionId: string;
  step: StepExecutionView;
  stepIndex: number;
  canExecute: boolean;
}

export function StepExecutionCard({ inspectionId, step, stepIndex, canExecute }: Props) {
  const expandedStepIds = useProcedureExecutionStore((s) => s.expandedStepIds);
  const toggleStep = useProcedureExecutionStore((s) => s.toggleStep);
  const dirtyStepIds = useProcedureExecutionStore((s) => s.dirtyStepIds);

  const isExpanded = expandedStepIds.has(step.stepExecutionId);
  const isDirty = dirtyStepIds.has(step.stepExecutionId);
  const statusCfg = STATUS_CONFIG[step.status] ?? STATUS_CONFIG.NOT_STARTED;

  return (
    <div
      className={`rounded-lg border transition-colors ${isExpanded ? 'border-border bg-card' : 'border-border/60 bg-muted/10 hover:bg-muted/20'}`}
    >
      {/* Header row */}
      <button
        type="button"
        onClick={() => toggleStep(step.stepExecutionId)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left min-h-[48px]"
        aria-expanded={isExpanded}
      >
        {/* Step number */}
        <span className="mt-0.5 shrink-0 text-xs font-mono text-muted-foreground w-5 text-right">
          {stepIndex}.
        </span>

        {/* Status icon */}
        <FontAwesomeIcon
          icon={statusCfg.icon}
          className={`mt-0.5 h-4 w-4 shrink-0 ${statusCfg.cls}`}
          title={statusCfg.label}
        />

        {/* Title + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`text-sm font-medium ${isExpanded ? 'text-foreground' : 'text-foreground/80'}`}
            >
              {step.title}
            </span>
            {step.isMandatory && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Mandatory
              </span>
            )}
            {step.evidenceRequired && (
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700">
                Evidence
              </span>
            )}
            {step.passFailRequired && (
              <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-700">
                Pass/Fail
              </span>
            )}
            {isDirty && (
              <span className="flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-xs text-amber-700">
                <FontAwesomeIcon icon={faTriangleExclamation} className="h-2.5 w-2.5" />
                Unsaved
              </span>
            )}
          </div>
        </div>

        {/* Auth warning + chevron */}
        <div className="flex shrink-0 items-center gap-2">
          {step.authorizationRequired && (
            <FontAwesomeIcon
              icon={faShieldHalved}
              className="h-3.5 w-3.5 text-amber-500"
              title="Authorization required"
            />
          )}
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="h-3 w-3 text-muted-foreground transition-transform"
          />
        </div>
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <StepExecutionBody
          inspectionId={inspectionId}
          step={step}
          canExecute={canExecute}
          isExpanded={isExpanded}
        />
      )}
    </div>
  );
}
